import Badge from "@/components/Badge";
import Text from "@/components/Text";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { COLUMNS_GRID_BUILDER } from "@/lib/constants";
import { getColorKrunshScore } from "@/lib/utils";
import {
  createGrid,
  createGridState,
  resetCreateGridState,
} from "@/store/createGridState";
import { getGridsState } from "@/store/getGridsState";
import {
  getRestaurantsInGrid,
  getRestaurantsInGridState,
  resetRestaurantsInGridState,
} from "@/store/getRestaurantsInGridState";
import {
  resetupdateGridState,
  updateGrid,
  updateGridState,
} from "@/store/updateGridState";
import { userRestaurantsState } from "@/store/userRestaurantsState";
import { BikeIcon, StarIcon } from "lucide-react";
import { useEffect, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";

const OPTIONS_PLATFORM = [
  {
    label: "alert_option_all_platforms",
    value: "all",
  },
  {
    label: "UBEREATS",
    value: "UBEREATS",
  },
  {
    label: "DELIVEROO",
    value: "DELIVEROO",
  },
];

const CreateOrEditGrid: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { gridId } = useParams<{ gridId: string }>();
  const { data: userRestaurants } = userRestaurantsState();
  const { data: restaurantsInGrid } = getRestaurantsInGridState();
  const { data: createGridData, isLoading: createGridDataIsloading } =
    createGridState();
  const { data: updateGridData, isLoading: updateGridDataIsLoading } =
    updateGridState();
  const { data: getGridsData } = getGridsState();

  const [restaurantsSelected, setRestaurantsSelected] = useState<string[]>(
    userRestaurants ? userRestaurants.map((restaurant) => restaurant.id) : [],
  );
  const [view, setView] = useState<"select-restaurants" | "name-grid">(
    "select-restaurants",
  );
  const [nameGrid, setNameGrid] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [platformSelected, setPlatformSelected] = useState("all");

  useEffect(() => {
    return () => {
      resetRestaurantsInGridState();
      resetCreateGridState();
      resetupdateGridState();
    };
  }, []);

  useEffect(() => {
    if (createGridDataIsloading) {
      toast(t("creation_grid"));
    }

    if (createGridData) {
      navigate(`/grid-builder/${createGridData.id}`);
      resetCreateGridState();
    }
  }, [createGridData, createGridDataIsloading]);

  useEffect(() => {
    if (updateGridDataIsLoading) {
      toast(t("updating_grid"));
    }

    if (updateGridData) {
      navigate("/grid-builder");
      resetupdateGridState();
    }
  }, [updateGridData, updateGridDataIsLoading]);

  useEffect(() => {
    if (gridId) {
      getRestaurantsInGrid({ gridId });
    }
  }, [gridId]);

  useEffect(() => {
    setRestaurantsSelected(
      restaurantsInGrid
        ? restaurantsInGrid.data.filter((r) => r !== null).map((r) => r.id)
        : [],
    );
  }, [restaurantsInGrid]);

  useEffect(() => {
    if (gridId && getGridsData) {
      const grid = getGridsData?.find((grid) => grid.id === gridId);
      if (grid) {
        setNameGrid(grid.name);
      }
    }
  }, [gridId, getGridsData]);

  let mode: "create" | "edit" = "create";

  if (gridId) {
    mode = "edit";
  }

  if (!userRestaurants) return null;

  let restaurantsFiltered = userRestaurants;

  if (platformSelected !== "all") {
    restaurantsFiltered = restaurantsFiltered.filter(
      (restaurant) => restaurant.platform === platformSelected,
    );
  }

  if (search) {
    restaurantsFiltered = restaurantsFiltered.filter((restaurant) =>
      restaurant.name.toLowerCase().includes(search.toLowerCase()),
    );
  }

  restaurantsFiltered.sort((a, b) => a.name.localeCompare(b.name));

  const someRestaurantsSelected = restaurantsFiltered.some((restaurant) =>
    restaurantsSelected.includes(restaurant.id),
  );

  return (
    <div
      style={{
        height: "calc(100dvh - var(--height-header))",
      }}
      className="bg-[#F7F8F9] p-[80px] overflow-y-auto overflow-x-hidden flex flex-col"
    >
      {view === "select-restaurants" && (
        <>
          <Text fontSize={20} fontWeight={600} className="mb-[8px]">
            {mode === "create"
              ? t("grid_builder_select_restaurants_title")
              : t("grid_builder_edit_grid_title")}
          </Text>
          <Text fontSize={16} fontWeight={400}>
            {t("grid_builder_select_restaurants_subtitle")}
          </Text>

          <div className="flex gap-[16px] mt-[32px] items-center">
            <Input
              placeholder={t("research_restaurant")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-[260px]"
            />
            <Select
              onValueChange={(value) => setPlatformSelected(value)}
              value={platformSelected}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OPTIONS_PLATFORM.map(({ label, value }) => (
                  <SelectItem key={`platform-${value}`} value={value}>
                    {t(label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant="secondaryFilled"
              className="w-[min-content] "
              onClick={() => {
                if (someRestaurantsSelected) {
                  setRestaurantsSelected([]);
                } else {
                  setRestaurantsSelected(
                    restaurantsFiltered.map((restaurant) => restaurant.id),
                  );
                }
              }}
            >
              {someRestaurantsSelected ? t("unselect_all") : t("select_all")}
            </Button>
          </div>

          <div className="flex gap-[32px] w-[100%]  mt-[16px]">
            <div className="flex flex-col gap-[12px] w-[100%]">
              {restaurantsFiltered.map((restaurant) => (
                <div
                  onClick={() => {
                    if (restaurantsSelected.includes(restaurant.id)) {
                      setRestaurantsSelected(
                        restaurantsSelected.filter(
                          (id) => id !== restaurant.id,
                        ),
                      );
                    } else {
                      setRestaurantsSelected([
                        ...restaurantsSelected,
                        restaurant.id,
                      ]);
                    }
                  }}
                  key={restaurant.id}
                  className="flex bg-[white] px-[24px] py-[16px] rounded-[12px] gap-[12px] items-center cursor-pointer"
                  style={{
                    boxShadow: "0px 8px 12px 0px #0000000A",
                  }}
                >
                  <Checkbox
                    checked={restaurantsSelected.includes(restaurant.id)}
                  />
                  <div className="flex flex-col gap-[12px]">
                    <div className="flex gap-[12px]">
                      <img
                        className="h-[40px] w-[40px] object-cover rounded-[3px]"
                        src={restaurant?.imageUrl ?? ""}
                      />
                      <div className="flex flex-col gap-[4px]">
                        <Text fontSize={14} fontWeight={600}>
                          {restaurant.name}
                        </Text>
                        <Text fontSize={12} fontWeight={400}>
                          {restaurant.address}
                        </Text>
                      </div>
                    </div>
                    <div className="flex gap-[8px]">
                      <img
                        className="h-[24px] w-[24px] rounded-[50%] object-cover"
                        src={`/partner/${restaurant.platform.toLowerCase()}.png`}
                      />
                      <div
                        className="h-[24px] w-[24px] ml-[-20px] bg-[white] rounded-[50%] flex items-center justify-center"
                        style={{
                          border: `2px solid ${getColorKrunshScore(restaurant.krunshScore)}`,
                        }}
                      >
                        <Text
                          fontSize={12}
                          fontWeight={600}
                          color={getColorKrunshScore(restaurant.krunshScore)}
                        >
                          {restaurant.krunshScore}
                        </Text>
                      </div>
                      <Badge>
                        <StarIcon height={16} width={16} color="#758195" />
                        <Text fontWeight={600} fontSize={14}>
                          {restaurant.rating}/5
                        </Text>
                      </Badge>
                      <Badge>
                        <BikeIcon height={16} width={16} color="#758195" />
                        <Text
                          fontWeight={600}
                          fontSize={14}
                        >{`${restaurant.deliveryTime} min`}</Text>
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-[white] p-[24px] rounded-[12px] h-[min-content] flex flex-col gap-[16px]">
              <Text fontSize={20} fontWeight={600}>
                {t("selection")}
              </Text>
              <div className="flex gap-[8px]">
                <Text fontSize={16} fontWeight={600} color="#8B92A2">
                  {t("stores")}
                </Text>
                <Text fontSize={16} fontWeight={600} color="#414651">
                  {restaurantsSelected.length}
                </Text>
              </div>
              <Button
                disabled={restaurantsSelected.length === 0}
                className="w-[230px]"
                onClick={() => {
                  setView("name-grid");
                }}
              >
                {t("continue")}
              </Button>
            </div>
          </div>
        </>
      )}

      {view === "name-grid" && (
        <>
          <Text fontSize={20} fontWeight={600} className="mb-[8px]">
            {mode === "create" ? t("create_your_view") : t("update_your_view")}
          </Text>
          <Text fontSize={16} fontWeight={400}>
            {t("name_your_view")}
          </Text>

          <div className="my-[32px]">
            <label htmlFor="name" className="mb-[4px]">
              <Text fontSize={14} fontWeight={600}>
                {t("name_of_your_view")}
              </Text>
            </label>
            <Input
              id="name"
              placeholder={t("enter_view_name")}
              className="w-[260px]"
              value={nameGrid}
              onChange={(e) => setNameGrid(e.target.value)}
            />
          </div>

          <Button
            disabled={
              !nameGrid || createGridDataIsloading || updateGridDataIsLoading
            }
            className="w-[min-content]"
            onClick={() => {
              if (mode === "create") {
                createGrid({
                  name: nameGrid,
                  restaurantIds: restaurantsSelected,
                  columns: COLUMNS_GRID_BUILDER,
                });
              }

              if (mode === "edit" && gridId && getGridsData) {
                const grid = getGridsData?.find((grid) => grid.id === gridId);
                if (grid) {
                  setNameGrid(grid.name);
                  updateGrid({
                    grid: {
                      ...grid,
                      name: nameGrid,
                    },
                    restaurantsId: restaurantsSelected,
                  });
                }
              }
            }}
          >
            {mode === "create" ? t("create_the_view") : t("update_the_view")}
          </Button>
        </>
      )}
    </div>
  );
};

export default CreateOrEditGrid;
